import React from "react";
import { ArrowUpRight, Instagram } from "lucide-react";

interface AuthShellProps {
  children: React.ReactNode;
}

/** Shared frame for all authentication stages. */
export const AuthShell: React.FC<AuthShellProps> = ({ children }) => (
  <main
    className="min-h-screen overflow-x-hidden bg-surface lg:grid lg:grid-cols-[0.76fr_1.24fr]"
    data-brand="diaria-brasil"
  >
    <section className="hidden min-h-screen border-r border-border bg-surface-canvas lg:flex lg:flex-col">
      <div className="flex min-h-screen flex-col py-10 pl-8 pr-6 xl:py-12 xl:pl-10 2xl:pl-16 2xl:pr-8">
        <div className="ml-auto w-[calc(100%-2rem)] max-w-[520px]">
          <img
            src="/logo-diaria-brasil-transparent.png"
            alt="Diária Brasil"
            width={359}
            height={144}
            className="h-auto w-48 object-contain object-left"
          />
          <div className="mt-20 translate-y-6 xl:mt-24">
            <div className="flex items-start gap-5">
              <div aria-hidden="true" className="mt-1 flex h-[104px] w-[3px] shrink-0 flex-col overflow-hidden rounded-full">
                <span className="block h-1/2 bg-diaria-cyan" />
                <span className="block h-[35%] bg-diaria-lime" />
                <span className="block h-[15%] bg-[#E9A33A]" />
              </div>
              <div>
                <h1 className="text-[40px] font-bold leading-[1.08] tracking-tight text-foreground xl:text-[44px]">
                  Portal Operacional
                </h1>
                <p className="mt-5 max-w-[430px] text-base leading-7 text-foreground-muted">
                  Acesse os sistemas que apoiam a operação, a gestão e a auditoria da rede hoteleira.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-8 mt-auto ml-auto w-[calc(100%-2rem)] max-w-[520px]">
          <InstitutionalLinks />
        </div>
      </div>
    </section>
    <section className="min-h-screen bg-surface">
      <header className="flex min-h-[72px] min-w-0 items-center justify-between gap-4 border-b border-border px-5 lg:hidden">
        <img src="/logo-diaria-brasil-transparent.png" alt="Diária Brasil" className="h-[50px] w-36 shrink-0 object-contain object-left" />
        <span className="hidden truncate text-right text-xs font-semibold text-foreground-muted min-[350px]:block">
          Portal Operacional
        </span>
      </header>
      <div className="flex min-h-[calc(100vh-72px)] items-start justify-center px-4 pb-10 pt-12 min-[360px]:px-5 sm:px-8 lg:min-h-screen lg:items-center lg:py-10 lg:pl-12 lg:pr-24 xl:pl-16 xl:pr-32">
        <div className="w-full max-w-[460px] lg:max-w-[496px]">
          <div className="rounded-[1.5rem] border border-border bg-surface p-5 shadow-overlay min-[360px]:p-7 sm:p-8 lg:p-10">
            {children}
          </div>
          <nav aria-label="Links institucionais" className="mt-6 grid grid-cols-2 gap-2 lg:hidden">
            <a href="https://www.instagram.com/diariabrasil/" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center justify-center gap-1 rounded-xl border border-border px-3 py-3 text-center text-xs font-semibold text-foreground-muted transition-colors hover:bg-surface-canvas hover:text-brand-dark focus-visible:text-brand-dark">
              <Instagram aria-hidden="true" data-testid="mobile-instagram-icon" className="shrink-0" size={14} />
              <span>Diária Brasil</span>
              <ArrowUpRight aria-hidden="true" className="shrink-0" size={13} />
            </a>
            <a href="https://www.instagram.com/facilitalavanderiaoficial/" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center justify-center gap-1 rounded-xl border border-border px-3 py-3 text-center text-xs font-semibold text-foreground-muted transition-colors hover:bg-surface-canvas hover:text-brand-dark focus-visible:text-brand-dark">
              <Instagram aria-hidden="true" data-testid="mobile-instagram-icon" className="shrink-0" size={14} />
              <span>Facilita</span>
              <ArrowUpRight aria-hidden="true" className="shrink-0" size={13} />
            </a>
          </nav>
        </div>
      </div>
    </section>
  </main>
);

const InstitutionalLinks: React.FC = () => (
  <nav aria-label="Nossas marcas" className="max-w-md border-t border-border pt-6">
    <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">Nossas marcas</h2>
    <ul className="grid max-w-md gap-1">
      <li>
        <a href="https://www.instagram.com/diariabrasil/" target="_blank" rel="noopener noreferrer" className="group -mx-2 flex min-h-11 items-center justify-between rounded-lg px-2 transition-colors hover:bg-surface focus:outline-none focus:ring-2 focus:ring-focus/20">
          <span className="text-sm font-semibold text-foreground">Diária Brasil</span>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground-muted transition-colors group-hover:text-brand-dark">
            Instagram <ArrowUpRight aria-hidden="true" size={14} />
          </span>
        </a>
      </li>
      <li>
        <a href="https://www.instagram.com/facilitalavanderiaoficial/" target="_blank" rel="noopener noreferrer" className="group -mx-2 flex min-h-11 items-center justify-between rounded-lg px-2 transition-colors hover:bg-surface focus:outline-none focus:ring-2 focus:ring-focus/20">
          <span className="text-sm font-semibold text-foreground">Facilita Lavanderia</span>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground-muted transition-colors group-hover:text-brand-dark">
            Instagram <ArrowUpRight aria-hidden="true" size={14} />
          </span>
        </a>
      </li>
    </ul>
  </nav>
);
